import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Loader, Message, Segment, Header, Grid } from "semantic-ui-react";

import { loadCustomerBalance } from "../actions/balance"; // Adjust the import path to your actions file
import { buildInvoiceNumberWithBeer } from "utilities/dist/invoice/helpers";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";
import { CompanyFeatures } from "utilities/dist/company/constants";
import { format } from "date-fns";
import { parseServerTimestampForClient } from "../../common/helpers";
import { DateFormat } from "utilities/dist/invoice/constants";
import { formatAmount } from "utilities/dist/helpers";
import InvoiceComponentStatus from "../../invoice/components/status";

export const CustomerContainerBalance = ({ customerId }) => {
    const dispatch = useDispatch();

    const loading = useSelector(state => state.customerBalance.loading[customerId]);
    const error = useSelector(state => state.customerBalance.error[customerId]);
    const balanceInfo = useSelector(state => state.customerBalance.byId[customerId]);
    const company = useSelector(state => state.auth.user.company);
    const isCustomerInvoiceNumberingActive = isCompanyFeatureActive(
        company,
        CompanyFeatures.CUSTOMER_INVOICE_NUMBERING
    );

    useEffect(() => {
        if (!balanceInfo && customerId) {
            dispatch(loadCustomerBalance(customerId));
        }
    }, [dispatch, customerId, balanceInfo]);

    if (loading) {
        return (
            <Loader active inline="centered">
                Loading
            </Loader>
        );
    }

    if (error) {
        return (
            <Message
                error
                icon="exclamation triangle"
                header="Failed to load balance information."
                content={`If you report this issue, please mention this error: "${error}"`}
            />
        );
    }

    return <CustomerBalanceDetails {...{ balanceInfo, isCustomerInvoiceNumberingActive }} />;
};

export const CustomerBalanceDetails = ({ balanceInfo, isCustomerInvoiceNumberingActive }) => {
    if (!balanceInfo) {
        // Show an informational message when no balance information is found
        return (
            <Message
                info
                header="No Balance Information"
                content="No balance information available for this customer."
            />
        );
    }

    return (
        <Grid>
            <Grid.Row>
                <Grid.Column mobile={16} tablet={8} computer={8}>
                    <Header as="h3" attached="top">
                        Owing: {formatAmount(balanceInfo.summary.owing)}
                        <Header.Subheader>
                            List of Due/Partially paid invoices of this customer
                        </Header.Subheader>
                        <Header.Subheader>{format(new Date(), DateFormat)}</Header.Subheader>
                    </Header>
                    <Segment attached>
                        <Grid>
                            {balanceInfo.invoices.map(invoice => {
                                const invoiceNumber = buildInvoiceNumberWithBeer(
                                    invoice,
                                    isCustomerInvoiceNumberingActive
                                );
                                return (
                                    <Grid.Row key={invoice.id}>
                                        <Grid.Column width={10}>
                                            <Header as="h5">
                                                <Link to={`/invoice/${invoiceNumber}`}>
                                                    <Header.Content>
                                                        #{invoiceNumber}
                                                    </Header.Content>
                                                </Link>
                                                <Header.Subheader>
                                                    {format(
                                                        parseServerTimestampForClient(
                                                            invoice.invoiced_at
                                                        ),
                                                        DateFormat
                                                    )}{" "}
                                                    | {formatAmount(invoice.total)}
                                                </Header.Subheader>
                                                <Header.Subheader as="h6">
                                                    {invoice.due_at
                                                        ? `Due on ${format(
                                                              parseServerTimestampForClient(
                                                                  invoice.due_at
                                                              ),
                                                              DateFormat
                                                          )}`
                                                        : "On Delivery"}
                                                </Header.Subheader>
                                            </Header>
                                        </Grid.Column>
                                        <Grid.Column width={6} textAlign="right">
                                            <InvoiceComponentStatus status={invoice.status} />
                                        </Grid.Column>
                                    </Grid.Row>
                                );
                            })}
                        </Grid>
                    </Segment>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );
};
